/**
 * Evaluator for `HMIComputedFormula` (see `types.ts`) - fields whose value is
 * derived client-side from other fields, never written by the device/edge.
 * Structured data (not a parsed string expression), same philosophy as
 * `rules.ts`: every formula built in the layout builder's "Computed Fields"
 * tab is guaranteed well-formed.
 */
import { HMIComputedFormula, HMIComputedOperand, HMIField, HMILiveValues } from './types';

function toNumber(value: unknown): number {
  if (typeof value === 'number') return Number.isNaN(value) ? 0 : value;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
    return Number(value);
  }
  return 0;
}

/**
 * For a `datatype: "array"` grid field (rows x cols of cells, each either
 * `0`/off or a nonzero power level - see `ArrayGridField`), counts how many
 * cells are ON (non-zero) in each row, and returns the highest count found
 * across all rows. Returns `0` when the field is missing, isn't a grid, or
 * has no value yet.
 */
export function gridMaxRowOnCount(field: HMIField | undefined, values: HMILiveValues): number {
  if (!field) return 0;
  const cells = values[field.id];
  if (!Array.isArray(cells)) return 0;

  const cols = field.gridCols ?? field.arrayLength ?? cells.length ?? 1;
  const rows = field.gridRows ?? 1;
  let max = 0;
  for (let r = 0; r < rows; r += 1) {
    let onCount = 0;
    for (let c = 0; c < cols; c += 1) {
      if (toNumber(cells[r * cols + c]) !== 0) onCount += 1;
    }
    if (onCount > max) max = onCount;
  }
  return max;
}

function operandLabel(operand: HMIComputedOperand, fieldsById: Map<string, HMIField>): string {
  if (operand.kind === 'literal') return String(operand.value);
  if (operand.kind === 'field') return fieldsById.get(operand.fieldId)?.caption ?? operand.fieldId;
  const label = operand.fn === 'gridMaxRowOnCount' ? 'highest ON-cell count in any row of' : operand.fn;
  return `${label} ${fieldsById.get(operand.fieldId)?.caption ?? operand.fieldId}`;
}

/** Human-readable description of a formula, used as the display text in the "Computed Fields" panel. */
export function describeComputedFormula(formula: HMIComputedFormula, fields: HMIField[]): string {
  const fieldsById = new Map(fields.map((f) => [f.id, f]));
  const target = fieldsById.get(formula.targetFieldId)?.caption ?? formula.targetFieldId;
  return `${target} = ${operandLabel(formula.left, fieldsById)} ${formula.operator} ${operandLabel(
    formula.right,
    fieldsById
  )}`;
}

function resolveOperand(
  operand: HMIComputedOperand,
  fieldsById: Map<string, HMIField>,
  values: HMILiveValues
): number {
  if (operand.kind === 'literal') return operand.value;
  if (operand.kind === 'field') return toNumber(values[operand.fieldId]);
  // operand.kind === 'aggregate'
  if (operand.fn === 'gridMaxRowOnCount') {
    return gridMaxRowOnCount(fieldsById.get(operand.fieldId), values);
  }
  return 0;
}

/** Every field id a formula reads from or writes to, used to decide when it needs re-checking/cleanup. */
export function computedFormulaFieldIds(formula: HMIComputedFormula): string[] {
  const ids = new Set<string>([formula.targetFieldId]);
  [formula.left, formula.right].forEach((operand) => {
    if (operand.kind !== 'literal') ids.add(operand.fieldId);
  });
  return Array.from(ids);
}

/** Whether a formula reads from or writes to the given field id (e.g. before deleting that field). */
export function computedFormulaReferencesField(formula: HMIComputedFormula, fieldId: string): boolean {
  return computedFormulaFieldIds(formula).includes(fieldId);
}

/** Evaluates one formula's `left <operator> right` against the current values, rounded per `decimals`. */
export function evaluateComputedFormula(
  formula: HMIComputedFormula,
  fields: HMIField[],
  values: HMILiveValues
): number {
  const fieldsById = new Map(fields.map((f) => [f.id, f]));
  const left = resolveOperand(formula.left, fieldsById, values);
  const right = resolveOperand(formula.right, fieldsById, values);

  let result: number;
  switch (formula.operator) {
    case '+':
      result = left + right;
      break;
    case '-':
      result = left - right;
      break;
    case '*':
      result = left * right;
      break;
    case '/':
      result = right === 0 ? 0 : left / right;
      break;
    default:
      result = 0;
  }

  const target = fieldsById.get(formula.targetFieldId);
  const decimals = formula.decimals ?? target?.decimals ?? 0;
  const factor = 10 ** decimals;
  return Math.round(result * factor) / factor;
}

/**
 * Re-evaluates every computed formula in the template against `values` and
 * writes each result into its `targetFieldId`. Each formula reads from the
 * original `values` snapshot (not from other formulas' freshly-computed
 * results in the same pass) - there is intentionally no cross-formula
 * chaining, to keep evaluation order-independent. Returns the original
 * object (not a copy) when nothing actually changed, so callers like
 * `useHmiLiveChannel` can use this directly inside a `setState` updater
 * without causing redundant re-renders.
 */
export function applyComputedFormulas(
  formulas: HMIComputedFormula[] | undefined,
  fields: HMIField[],
  values: HMILiveValues
): HMILiveValues {
  if (!formulas || formulas.length === 0) return values;

  let next: HMILiveValues | undefined;
  formulas.forEach((formula) => {
    const result = evaluateComputedFormula(formula, fields, values);
    if (values[formula.targetFieldId] !== result) {
      next = next ?? { ...values };
      next[formula.targetFieldId] = result;
    }
  });
  return next ?? values;
}
